import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import safeNoop from "../lib/safeNoop";
import { KeyRound, Copy, RefreshCw, CheckCircle2, Eye, EyeOff } from "lucide-react";

/**
 * Bridge token panel. Lives on the Settings page as a small, intentionally
 * unflashy card — the bridge token is sensitive and shouldn't compete
 * visually with the rest of the form.
 *
 *   - GET  /api/sites/{id}/bridge-token       — fetched on mount; lazy-creates
 *   - POST /api/sites/{id}/bridge-token/rotate — issues a fresh one
 *
 * The token is masked by default; the user must click "Reveal" to see it,
 * which discourages shoulder-surfing during demos. Copy + Rotate are
 * always one click. Rotating immediately invalidates any bridges still
 * using the old token (the backend enforces that — we only inform the
 * user via the helper text).
 */
export default function BridgeTokenCard({ siteId }) {
  const [token, setToken] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/sites/${siteId}/bridge-token`);
      setToken(data.bridge_token);
      setErr(null);
    } catch (e) {
      setErr(e.userMessage || "Could not load bridge token");
    }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard blocked (insecure context, denied permission). The token
      // is on screen when revealed so the user can still select-and-copy.
      safeNoop("settings.bridge-token-copy-blocked", e);
    }
  };

  const rotate = async () => {
    setRotating(true); setErr(null);
    try {
      const { data } = await api.post(`/sites/${siteId}/bridge-token/rotate`);
      setToken(data.bridge_token);
      setConfirmRotate(false);
      setRevealed(true); // show it once so the user can copy it
    } catch (e) {
      setErr(e.userMessage || "Failed to rotate token");
    } finally {
      setRotating(false);
    }
  };

  // Mask: keep the first 4 and last 4 chars visible so the user can sanity-
  // check at a glance without exposing the full string.
  const displayed = !token
    ? "—"
    : revealed
      ? token
      : `${token.slice(0, 4)}${"·".repeat(Math.max(0, token.length - 8))}${token.slice(-4)}`;

  return (
    <section className="card-base p-6" data-testid="bridge-token-card">
      <div className="flex items-start justify-between gap-3 flex-col sm:flex-row">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Bridge token
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Used by a local bridge (Mac, Raspberry Pi, Home Assistant, n8n) to push HomeWizard or other device readings into this site. Keep it secret — anyone with this token can post data on your behalf.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <code
          className="font-mono text-xs px-3 py-2 rounded-md bg-zinc-950 border border-border break-all flex-1 min-w-[200px]"
          data-testid="bridge-token-value"
        >
          {displayed}
        </code>
        <button
          onClick={() => setRevealed((v) => !v)}
          className="px-2.5 py-2 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5"
          data-testid="bridge-token-reveal"
        >
          {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {revealed ? "Hide" : "Reveal"}
        </button>
        <button
          onClick={copy}
          disabled={!token}
          className="px-2.5 py-2 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="bridge-token-copy"
        >
          {copied ? <CheckCircle2 className="w-3 h-3 text-[#10B981]" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
        {confirmRotate ? (
          <button
            onClick={rotate}
            disabled={rotating}
            className="px-2.5 py-2 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="bridge-token-rotate-confirm"
          >
            <RefreshCw className={`w-3 h-3 ${rotating ? "animate-spin" : ""}`} />
            {rotating ? "Rotating…" : "Confirm rotate"}
          </button>
        ) : (
          <button
            onClick={() => setConfirmRotate(true)}
            className="px-2.5 py-2 rounded-md border border-border hover:bg-secondary text-xs inline-flex items-center gap-1.5"
            data-testid="bridge-token-rotate"
          >
            <RefreshCw className="w-3 h-3" /> Rotate
          </button>
        )}
      </div>

      {err && (
        <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5 mt-3" data-testid="bridge-token-error">
          {err}
        </div>
      )}

      <div className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
        Endpoint · <code className="font-mono">POST /api/sites/{siteId}/ingest</code> &nbsp;
        Header · <code className="font-mono">Authorization: Bearer &lt;token&gt;</code>
        <br />
        Rotating immediately invalidates any bridge still using the previous value.
      </div>
    </section>
  );
}
